import Immutable, { Record } from "immutable";
import { BlockConfig, RequiredState } from "../types";

export interface BaseMessageRecordAttributes {
  displayType?: string;
  id?: string;
  isLoading?: boolean;
  locked?: boolean;
  multipleAllowed?: boolean;
  reviewableMessage?: boolean;
  variableId?: string;
  variablesData?: Immutable.List<unknown>;
}

const baseFields: BaseMessageRecordAttributes = {
  id: null,
  isLoading: false,
  locked: false,
  multipleAllowed: true,
  reviewableMessage: false,
  variableId: null
};

export interface BaseMessageRecordInstance {
  type: string;
  invalidFields: Immutable.List<string>;
}

export type BlockConfigGetter = (state: RequiredState) => BlockConfig;

interface BaseMessageRecordClass<T> {
  new (values: T): Record.Instance<T> & Readonly<T> & BaseMessageRecordInstance;
  getInvalidFields(message: unknown): Immutable.List<string>;
  blockConfig: BlockConfigGetter;
}

type KnownAttributes = BaseMessageRecordAttributes & {type: string};

export default function BaseMessageRecord<T>(
  defaultAttributes: {type: string} & T
): BaseMessageRecordClass<T> {
  const newDefaultAttributes = {
    ...baseFields,
    ...defaultAttributes
  };

  class MessageRecord extends Immutable.Record(newDefaultAttributes as KnownAttributes) {
    cache: {
      invalidFields?: Immutable.List<string>;
    };

    get invalidFields(): Immutable.List<string> {
      const { getInvalidFields } = this.constructor as BaseMessageRecordClass<T>;

      // We store results of getters' calculations in this.cache
      this.cache = this.cache || {};

      // Check if already was calculated
      if (this.cache.invalidFields) {
        return this.cache.invalidFields;
      }

      // If there's no validation function, return an empty List, but throw a warning
      if (!getInvalidFields) {
        window.console.warn(`getInvalidFields function not defined for "${this.type}" block`);

        return Immutable.List();
      }

      // Store results in cache
      this.cache.invalidFields = getInvalidFields(this);

      // And return them
      return this.cache.invalidFields;
    }
  }

  // this is a workaround for when Typescript doesn't want to convert types
  return MessageRecord as unknown as BaseMessageRecordClass<T>;
}
